import { Clock, MessageCircle, Trash2 } from "lucide-react";
import { patientWhatsAppUrl, buildAdminConfirmMessage, buildAdminRejectMessage, buildAdminContactMessage } from "@/lib/site";

export const STATUS_META = {
  pending: { label: "In attesa", cls: "bg-peach text-deeptaupe" },
  confirmed: { label: "Confermato", cls: "bg-deeptaupe text-ivory" },
  rejected: { label: "Rifiutato", cls: "bg-red-100 text-red-700" },
  cancelled: { label: "Annullato", cls: "bg-charcoal/10 text-charcoal/60" },
  expired: { label: "Scaduto", cls: "bg-taupe/30 text-charcoal/70" },
};

export const STATUS_FILTERS = [
  { key: "tutti", label: "Tutti" },
  { key: "pending", label: "Da confermare" },
  { key: "confirmed", label: "Confermati" },
  { key: "rejected", label: "Rifiutati" },
  { key: "cancelled", label: "Annullati" },
  { key: "expired", label: "Scaduti" },
];

function apptExpiryRome(iso) {
  try {
    return new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" }).format(new Date(iso));
  } catch (e) {
    return "";
  }
}

export default function ApptRow({ a, onConfirm, onCancel, onEdit, onDelete, mode, contact }) {
  const meta = STATUS_META[a.status] || STATUS_META.pending;
  const hasPhone = !!(a.phone && a.phone.trim());
  const openWa = (builder) => {
    const msg = builder({ name: a.name, service: a.service_name, date: a.date, time: a.start, end: a.end, mode });
    window.open(patientWhatsAppUrl(a.phone, msg), "_blank", "noopener,noreferrer");
  };
  const btn =
    "inline-flex items-center justify-center sm:justify-start gap-1.5 rounded-xl sm:rounded-none border sm:border-0 px-3 sm:px-0 py-2.5 sm:py-0 text-[12px] uppercase tracking-widest2 font-medium transition-colors";
  const neutral = `${btn} border-taupe/30 text-charcoal/70 sm:border-0 sm:text-charcoal/60`;
  const danger = `${btn} border-red-200 text-red-600 sm:border-0 sm:text-charcoal/60`;
  return (
    <div className="rounded-2xl bg-ivory px-3 py-2.5 sm:px-4 sm:py-3 mb-2" data-testid={`admin-appt-row-${a.mid}`}>
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <span className="font-display text-lg sm:text-xl text-deeptaupe w-20 sm:w-28">{a.start}–{a.end}</span>
        <span className="flex-1 min-w-[140px]">
          <span className="block text-[13px] sm:text-sm font-medium">{a.name}</span>
          <span className="block text-[12px] sm:text-[13px] text-charcoal/60">{a.service_name} · {a.phone || "—"}{a.email ? ` · ${a.email}` : ""}</span>
        </span>
        <span className={`text-[10px] sm:text-[11px] uppercase tracking-widest2 font-medium rounded-full px-2.5 py-0.5 sm:px-3 sm:py-1 ${meta.cls}`} data-testid={`admin-appt-status-${a.mid}`}>
          {meta.label}
        </span>
        <span className="text-[10px] uppercase tracking-widest2 text-charcoal/40">{a.source === "admin" ? "Manual" : "Sito"}</span>
      </div>
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 sm:gap-x-4 sm:gap-y-2 mt-2.5 pt-2.5 sm:mt-3 sm:pt-3 border-t border-taupe/15" data-testid={`admin-appt-actions-${a.mid}`}>
        {a.status === "pending" && a.expires_at && (
          <span className="col-span-2 text-[12px] text-charcoal/60 inline-flex items-center gap-1.5" data-testid={`admin-appt-expiry-${a.mid}`}>
            <Clock size={12} className="text-taupe" /> l&apos;orario si libera automaticamente il {apptExpiryRome(a.expires_at)}
          </span>
        )}
        {a.status === "pending" && (
          <button onClick={() => onConfirm(a, "confirmed")} data-testid={`admin-appt-confirm-${a.mid}`} className={`${btn} border-deeptaupe bg-deeptaupe text-ivory sm:bg-transparent sm:text-deeptaupe`}>
            Approva
          </button>
        )}
        {a.status === "pending" && (
          <button onClick={() => onConfirm(a, "rejected")} data-testid={`admin-appt-reject-${a.mid}`} className={danger}>
            Rifiuta
          </button>
        )}
        {a.status === "confirmed" && (
          <button onClick={() => onCancel(a, "cancelled")} data-testid={`admin-appt-cancel-${a.mid}`} className={danger}>
            Annulla
          </button>
        )}
        {hasPhone && (a.status === "pending" || a.status === "confirmed") && (
          <button onClick={() => openWa(buildAdminConfirmMessage)} data-testid={`admin-appt-wa-confirm-${a.mid}`} className={`${btn} border-[#1f7a4d]/30 text-[#1f7a4d] sm:border-0 sm:hover:underline`}>
            <MessageCircle size={13} /> Conferma su WhatsApp
          </button>
        )}
        {hasPhone && (a.status === "pending" || a.status === "rejected") && (
          <button onClick={() => openWa(buildAdminRejectMessage)} data-testid={`admin-appt-wa-reject-${a.mid}`} className={neutral}>
            <MessageCircle size={13} /> Rifiuta su WhatsApp
          </button>
        )}
        {hasPhone && (
          <button onClick={() => openWa(buildAdminContactMessage)} data-testid={`admin-appt-wa-contact-${a.mid}`} className={neutral}>
            <MessageCircle size={13} /> Contatta il paziente
          </button>
        )}
        {onEdit && (
          <button onClick={onEdit} data-testid={`admin-appt-edit-${a.mid}`} className={neutral}>
            Modifica
          </button>
        )}
        {onDelete && (
          <button onClick={() => onDelete(a)} data-testid={`admin-appt-delete-${a.mid}`} aria-label="Elimina definitivamente" className={`${btn} border-red-200 text-red-600 sm:border-0 sm:text-red-500/70`}>
            <Trash2 size={14} /> Elimina
          </button>
        )}
      </div>
    </div>
  );
}
